import DynamicAppointmentFormUtils
  from "./services/dynamic-appointment-form-utils.js";
import ConfirmationUtils from "../../js/confirmation-utils.es6.js";
import ApiUtils from "../../js/api-utils.es6.js";
import { BREAKPOINTS } from "./constants.js";
import { AnimationUtils, LuminisServiceUtils, SessionStorageUtils } from "@js/utils/utils-imports.es6.js";
import OverlayService from "@js/overlay-service.es6.js";
import { SEARCH_PARAM_KEYS, SESSION_STORAGE_KEYS, WORKFLOWS } from "@js/constants/constants-imports.es6.js";

export default class ProviderScheduleOnline {
  constructor() {
    const url = new URL(window.location.href);
    const workflow = url.searchParams.get(SEARCH_PARAM_KEYS.WORKFLOW);
    this.isSelectNewTimeWorkflow = workflow?.toLowerCase() === WORKFLOWS.SELECT_NEW_TIME.toLowerCase();

    this.npi = document.querySelector('.field--name-field-provider-npi-number');
    if (this.npi) {
      this.npi = this.npi.textContent;
    }
    this.loadingbox = document.querySelector('.schedule-online-loading-box');
    this.scheduleOnlineSection = document.querySelector('.schedule-online-section');
    this.scheduleOnlineBox = document.querySelector('.scheduling-online-box');
    this.initialProviderAllSlots = {
      'provider': {},
      'departmentAvailabilities': []
    }
    this.openVisitMnemonics = document.querySelectorAll('.field--name-field-open-visit-reason-mneumoni .field__item');
    this.directVisitMnemonics = document.querySelectorAll('.field--name-field-direct-visit-reason-mneumo .field__item');
    this.scheduleOnlineButton = document.querySelector('.schedule-online-button');
    this.currentWidth = window.innerWidth;
    this.isMobile = this.currentWidth < BREAKPOINTS.DESKTOP;
    this.overlayService = new OverlayService();
    this.mobileOverlay = document.querySelector('.right-side .mobile-overlay');
    this.mobileCloseButton = document.querySelector('.schedule-online-section .close-button');
    this.initSchedulingButton();
    this.luminisServiceUtils = new LuminisServiceUtils();
  }

  static get isProviderProfilePage() {
    return !!document.querySelector('.node--type-provider');
  }

  initSchedulingButton() {
    if (this.isMobile) {
      this.scheduleOnlineSection.classList.add('hidden');
    }

    this.scheduleOnlineButton.addEventListener('click', () => {
      if (this.isMobile) {
        this.scheduleOnlineSection.classList.remove('hidden');
        this.mobileOverlay.classList.add('active');

        const onOverlayClick = () => {
          this.scheduleOnlineSection.classList.add('hidden');
          this.overlayService.toggleOverlay(false);
        };

        this.overlayService.toggleOverlay(true, onOverlayClick, true);
      }

      ProviderScheduleOnline.scrollToScheduleOnlineSection();
    });

    this.mobileCloseButton.addEventListener('click', () => {
      this.closeMobileScheduleOnlineModal();
    });
  }

  async initScheduling($) {
    if (!this.isSelectNewTimeWorkflow) {
      ProviderScheduleOnline.clearSessionStorageOnStart();
    }

    // don't even make this slow call if the provider does not support mnemonics
    if ((this.openVisitMnemonics.length === 0 && this.directVisitMnemonics.length === 0) || !this.npi) {
      this.hideSchedulingItems();
      return;
    }

    // lookup if provider has availabilities
    this.loadingbox.classList.remove('hidden');

    const providerStartAndEndDates = await this.luminisServiceUtils.getProviderEligibleDates(this.npi);
    if (!providerStartAndEndDates) {
      this.hideSchedulingItems();
      return;
    }

    const mnemonicJson = await this.luminisServiceUtils.getProviderAvailabilities(this.npi, providerStartAndEndDates.startDate, providerStartAndEndDates.endDate);
    if (!mnemonicJson || mnemonicJson.length === 0) {
      this.hideSchedulingItems();
      return;
    } else {
      await this.setSessionValues(providerStartAndEndDates, mnemonicJson);

      const mnemonicList = await DynamicAppointmentFormUtils.buildMnemonicSelect(
        mnemonicJson,
        providerStartAndEndDates,
        null,
        null);

      if (mnemonicList.length === 0 || (mnemonicList.length === 1 && mnemonicList[0] === '')) {
        this.hideSchedulingItems();
        return;
      }

      const placeholder = document.querySelector('.field--name-field-condition-placeholder-labe .field--name-field-label .field__item').textContent;

      DynamicAppointmentFormUtils.initConditionSelect(
        $,
        '#field-form-element-condition',
        mnemonicList,
        this.triggerMnemonicSelected,
        $,
        placeholder);

      DynamicAppointmentFormUtils.visitTypeOnChange(this.initialProviderAllSlots, true);
      ProviderScheduleOnline.initLocationSelect($, '#location-select');
      DynamicAppointmentFormUtils.renderDatePicker(true, this.initialProviderAllSlots);
    }

    this.scheduleOnlineBox.classList.remove('hidden');
    this.loadingbox.classList.add('hidden');
    this.scheduleOnlineButton.classList.remove('hidden');

    if (this.isSelectNewTimeWorkflow) {
      this.handleSelectNewTimeWorkflow();
    }
  }

  handleSelectNewTimeWorkflow() {
    // Automatically go to the schedule online section
    this.scheduleOnlineButton.click();

    const mnemonic = SessionStorageUtils.getItem(SESSION_STORAGE_KEYS.MNEMONIC);
    if (mnemonic) {
      ConfirmationUtils.setCondition(mnemonic);
    }

    const appointmentType = SessionStorageUtils.getItem(SESSION_STORAGE_KEYS.APPOINTMENT_TYPE);
    if (appointmentType) {
      ConfirmationUtils.setVisitType(appointmentType);
    }

    const epicDepartmentId = SessionStorageUtils.getItem(SESSION_STORAGE_KEYS.EPIC_DEPARTMENT_ID);
    if (epicDepartmentId) {
      ConfirmationUtils.setLocation(epicDepartmentId);
    }
  }

  static scrollToScheduleOnlineSection() {
    const scrollTarget = document.querySelector('.schedule-online-section');
    if (!scrollTarget) {
      return;
    }

    AnimationUtils.scrollToElement(scrollTarget);
  }

  hideSchedulingItems() {
    this.scheduleOnlineSection.classList.add('hidden');
    this.scheduleOnlineButton.classList.add('hidden');

    const buttonsGroup = document.querySelector('.hero__buttons-group');
    if (buttonsGroup.children.length === 1) {
      // this means that the scheduling button is the only one left and we need to hide the styling if this is hidden.
      buttonsGroup.classList.add('hidden');
    }
  }

  async setSessionValues(providerStartAndEndDates, mnemonicJson) {
    const epicProviderId = await this.luminisServiceUtils.getProviderEpicId(this.npi);
    sessionStorage.setItem('resultType', 'provider');
    sessionStorage.setItem('epicProviderId', epicProviderId);
    sessionStorage.setItem(
      'providerSpecificStartAndEndDates',
      JSON.stringify(providerStartAndEndDates),
    );
    sessionStorage.setItem(
      'providerSpecificMnemonicLookup',
      JSON.stringify(mnemonicJson),
    );

    // get provider json api to pass to confirmation
    const provider = await ApiUtils.getProviderByNPIAndMapToConfirmation(this.npi);
    sessionStorage.setItem(
      'appointmentProvider',
      JSON.stringify(provider),
    );
  }

  triggerMnemonicSelected(event, $) {
    const mnemonic = event.id;
    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.MNEMONIC, mnemonic);

    const providerAllSlots = DynamicAppointmentFormUtils.setupMnemonicSlots(event);
    ConfirmationUtils.clearVisitType();
    ConfirmationUtils.disableVisitTypeIfEmpty(providerAllSlots);
    DynamicAppointmentFormUtils.disableVisitTypeBasedOnMnemonic(providerAllSlots);
    ProviderScheduleOnline.updateLocationSelect($, '#location-select', providerAllSlots);
    DynamicAppointmentFormUtils.visitTypeOnChange(providerAllSlots, true);
  }

  static initLocationSelect($, locationSelector) {
    const placeholderSelector = document.querySelector(
      '.field--name-field-location-placeholder-label .field--name-field-label .field__item');
    const $location = $(locationSelector);

    $location.html('').select2({
      placeholder: placeholderSelector.textContent,
      data: {}
    }).trigger('change');
  }

  static updateLocationSelect($, locationSelector, providerAllSlots) {
    const providerLocations = providerAllSlots.departmentAvailabilities;
    const placeholderSelector = document.querySelector(
      '.field--name-field-location-placeholder-label .field--name-field-label .field__item');
    const $location = $(locationSelector);

    const locationList = [{
      'text': '',
      'id': '',
      'address': '',
      'selected': false
    }];
    providerLocations.forEach(location => {
      locationList.push({
        'text': location.department.name,
        'id': location.department.id,
        'address': `${location.department.address.line1}, ${location.department.address.city}, ${location.department.address.state} ${location.department.address.zipCode}`,
        'selected': false
      });
    });

    $location.html('').select2({
      placeholder: placeholderSelector.textContent,
      data: locationList
    }).trigger('change');

    $location.on('select2:select', e => {
      const event = e.params.data;
      sessionStorage.setItem('epicDepartmentId', event.id);
      const appointmentLocationJson = {
        title: event.text,
        address: event.address,
        deptEpicId: event.id
      };
      sessionStorage.setItem('appointmentLocation', JSON.stringify(appointmentLocationJson));
      DynamicAppointmentFormUtils.clearDatePickerAndVisitType(true, providerAllSlots);
    });
  }

  static clearSessionStorageOnStart() {
    // make sure they start with an empty form
    sessionStorage.removeItem('referralRequirement');
    sessionStorage.removeItem('recent-condition');
    sessionStorage.removeItem('hasReferral');
    sessionStorage.removeItem('appointmentLocation');
    sessionStorage.removeItem('reservedTimeSlot');
    sessionStorage.removeItem('appointmentType');
    sessionStorage.removeItem('appointmentProvider');
    sessionStorage.removeItem('epicAppointmentType');
    sessionStorage.removeItem('epicDepartmentId');
    sessionStorage.removeItem('insuranceRequirement');
    sessionStorage.removeItem('providerAllSlots');
    sessionStorage.removeItem('appointmentProvider');
    sessionStorage.removeItem('reservationKey');
    sessionStorage.removeItem('resultType');
    sessionStorage.removeItem('epicProviderId');
  }

  closeMobileScheduleOnlineModal() {
    if (this.isMobile) {
      this.scheduleOnlineSection.classList.add('hidden');
      this.mobileOverlay.classList.remove('active');
      this.overlayService.toggleOverlay(false, undefined, false);
    }
  }
}

(async $ => {
  if (ProviderScheduleOnline.isProviderProfilePage) {
    const scheduleOnline = new ProviderScheduleOnline();
    await scheduleOnline.initScheduling($);
  }
})(jQuery, Drupal);
